import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus } from "lucide-react";
import { api, arabicError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LeaveCard } from "../components/EmployeeLeavesPanel";

const inputCls = "w-full h-11 rounded-lg border border-[#7AB2B2] px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E] bg-white";
const empty = { kind: "daily", from_date: "", to_date: "", date: "", from_time: "", to_time: "", note: "" };

export default function EmployeeLeaves() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const load = () => api.get("/employee/leaves").then((r) => setItems(r.data)).catch((e) => toast.error(arabicError(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = form.kind === "daily"
        ? { kind: "daily", from_date: form.from_date, to_date: form.to_date, note: form.note || undefined }
        : { kind: "hourly", date: form.date, from_time: form.from_time, to_time: form.to_time, note: form.note || undefined };
      await api.post("/employee/leaves", body);
      toast.success("تم إرسال طلب الإجازة إلى صاحب العمل"); setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(arabicError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="employee-leaves-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-[#333333] flex items-center gap-2"><CalendarDays className="text-[#09637E]" /> الإجازات</h1>
          <p className="text-[#666666] mt-1 text-sm">مرحباً {user?.first_name} — طلبات إجازتك تُرسل إلى {user?.store_name || "صاحب العمل"} للموافقة</p>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="new-leave-btn" className="h-11 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2">
          <Plus size={18} /> طلب إجازة
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-white border border-[#7AB2B2] rounded-xl p-4 space-y-3" data-testid="leave-form">
          <div className="grid grid-cols-2 gap-2 bg-[#EBF4F6] p-1 rounded-lg">
            <button type="button" onClick={() => set("kind", "daily")} data-testid="leave-kind-daily" className={`h-10 rounded-md font-bold text-sm ${form.kind === "daily" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}>إجازة يومية</button>
            <button type="button" onClick={() => set("kind", "hourly")} data-testid="leave-kind-hourly" className={`h-10 rounded-md font-bold text-sm ${form.kind === "hourly" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}>إجازة ساعية</button>
          </div>
          {form.kind === "daily" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-sm font-bold text-[#333333]">من تاريخ<input type="date" value={form.from_date} onChange={(e) => set("from_date", e.target.value)} className={inputCls} data-testid="leave-from-date" required /></label>
              <label className="text-sm font-bold text-[#333333]">إلى تاريخ<input type="date" value={form.to_date} onChange={(e) => set("to_date", e.target.value)} className={inputCls} data-testid="leave-to-date" required /></label>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="text-sm font-bold text-[#333333]">التاريخ<input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="leave-date" required /></label>
              <label className="text-sm font-bold text-[#333333]">من الساعة<input type="time" value={form.from_time} onChange={(e) => set("from_time", e.target.value)} className={inputCls} data-testid="leave-from-time" required /></label>
              <label className="text-sm font-bold text-[#333333]">إلى الساعة<input type="time" value={form.to_time} onChange={(e) => set("to_time", e.target.value)} className={inputCls} data-testid="leave-to-time" required /></label>
            </div>
          )}
          <textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="السبب / ملاحظة (اختياري)" maxLength={500} rows={2} className="w-full rounded-lg border border-[#7AB2B2] p-3 focus:outline-none focus:ring-2 focus:ring-[#09637E]" data-testid="leave-note" />
          <button type="submit" disabled={saving} className="w-full sm:w-auto h-11 px-6 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold disabled:opacity-60" data-testid="leave-submit">
            {saving ? "جارٍ الإرسال..." : "إرسال الطلب"}
          </button>
        </form>
      )}

      <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid="my-leaves-list">
        {items.length === 0 ? <div className="p-8 text-center text-[#666666]" data-testid="my-leaves-empty">لا توجد طلبات إجازة بعد</div>
          : items.map((lv) => <LeaveCard key={lv.id} lv={lv} />)}
      </div>
    </div>
  );
}
